/**
 * Swap Tab Component
 *
 * In-wallet token swap powered by Jupiter Ultra API.
 * Renders inside WalletModal as a tab content panel.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ArrowUpDown, Loader2, Check, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import type { Transaction, VersionedTransaction } from '@solana/web3.js';
import { Button } from '@/components/ui/button';
import { SwapTokenSelector, SOL_TOKEN, USDC_TOKEN } from '@/components/wallet/SwapTokenSelector';
import type { SwapToken } from '@/components/wallet/SwapTokenSelector';
import { useSwapExecution, useSwapAutoReset } from '@/hooks/useSwapExecution';
import type { WalletToken } from '@/hooks/useTokenBalances';
import { createSwapNotification, buildSwapAppNotification } from '@/lib/notifications';

interface SwapTabProps {
  walletAddress: string;
  signTransaction: (tx: Transaction | VersionedTransaction) => Promise<Transaction | VersionedTransaction>;
  connected: boolean;
  balance: number | null;
  splTokens: WalletToken[];
  formattedBalance: string;
  tokensLoading: boolean;
  isMobile: boolean;
  refetchBalance: () => void;
  refetchTokens: () => void;
}

// Format token amount for display
function formatAmount(rawAmount: string, decimals: number): string {
  const num = parseInt(rawAmount, 10);
  if (isNaN(num)) return '0';
  const result = num / Math.pow(10, decimals);
  if (result < 0.000001) return '<0.000001';
  if (result < 1) return result.toFixed(6);
  if (result < 1000) return result.toFixed(4);
  return result.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

// Convert UI amount to smallest unit string
function toSmallestUnit(uiAmount: string, decimals: number): string {
  const num = parseFloat(uiAmount);
  if (isNaN(num) || num <= 0) return '0';
  return Math.floor(num * Math.pow(10, decimals)).toString();
}

function getSolscanUrl(signature: string): string {
  return `https://solscan.io/tx/${signature}`;
}

export const SwapTab = ({
  walletAddress,
  signTransaction,
  connected,
  balance,
  splTokens,
  formattedBalance,
  isMobile,
  refetchBalance,
  refetchTokens,
}: SwapTabProps) => {
  const [inputToken, setInputToken] = useState<SwapToken>({ ...SOL_TOKEN, balance: balance ?? 0 });
  const [outputToken, setOutputToken] = useState<SwapToken>(USDC_TOKEN);
  const [inputAmount, setInputAmount] = useState('');

  const { state, fetchQuote, executeSwap, reset, isExecuting, canExecute } = useSwapExecution({
    signTransaction,
    walletAddress,
    connected,
  });

  // Auto-reset after success
  useSwapAutoReset(state.status, reset);

  // Refs for debounce and auto-refresh
  const quoteDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Update SOL balance on inputToken when balance changes
  useEffect(() => {
    if (inputToken.mint === SOL_TOKEN.mint) {
      setInputToken((prev) => ({ ...prev, balance: balance ?? 0 }));
    }
  }, [balance, inputToken.mint]);

  // Look up balance for selected input token from user's tokens
  const getTokenBalance = useCallback(
    (token: SwapToken): number | undefined => {
      if (token.mint === SOL_TOKEN.mint) return balance ?? undefined;
      const found = splTokens.find((t) => t.mint === token.mint);
      return found?.uiAmount;
    },
    [balance, splTokens]
  );

  const inputBalance = getTokenBalance(inputToken);
  const outputBalance = getTokenBalance(outputToken);

  // Usable input balance after fee reservation (for percentage button visibility)
  const usableInputBalance = useMemo(() => {
    if (inputBalance === undefined || inputBalance <= 0) return 0;
    if (inputToken.mint === SOL_TOKEN.mint) {
      return Math.max(0, inputBalance - 0.01);
    }
    return inputBalance;
  }, [inputBalance, inputToken.mint]);

  // Ref to prevent success effect from firing more than once per swap
  const lastHandledSignatureRef = useRef<string | null>(null);

  // Debounced quote fetching
  useEffect(() => {
    if (quoteDebounceRef.current) clearTimeout(quoteDebounceRef.current);

    if (!inputAmount || parseFloat(inputAmount) <= 0 || !inputToken || !outputToken) {
      if (state.status === 'ready' || state.status === 'quoting') {
        reset();
      }
      return;
    }

    const amount = toSmallestUnit(inputAmount, inputToken.decimals);
    if (amount === '0') return;

    quoteDebounceRef.current = setTimeout(() => {
      fetchQuote(inputToken.mint, outputToken.mint, amount);
    }, 500);

    return () => {
      if (quoteDebounceRef.current) clearTimeout(quoteDebounceRef.current);
    };
  }, [inputAmount, inputToken.mint, outputToken.mint, inputToken.decimals]);

  // Auto-refresh quote every 15s while idle/ready
  useEffect(() => {
    if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);

    if (
      state.status !== 'ready' ||
      !inputAmount ||
      parseFloat(inputAmount) <= 0 ||
      !inputToken ||
      !outputToken
    ) {
      return;
    }

    refreshIntervalRef.current = setInterval(() => {
      const amount = toSmallestUnit(inputAmount, inputToken.decimals);
      if (amount !== '0') {
        fetchQuote(inputToken.mint, outputToken.mint, amount);
      }
    }, 15_000);

    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    };
  }, [state.status, inputAmount, inputToken.mint, outputToken.mint, inputToken.decimals, fetchQuote]);

  // Refresh balances on success (guarded to fire exactly once per swap)
  useEffect(() => {
    if (
      state.status === 'success' &&
      state.signature &&
      state.signature !== lastHandledSignatureRef.current
    ) {
      lastHandledSignatureRef.current = state.signature;
      refetchBalance();
      refetchTokens();

      const sig = state.signature;
      toast.success('Swap complete', {
        action: {
          label: 'View on Solscan',
          onClick: () => window.open(getSolscanUrl(sig), '_blank'),
        },
      });

      // Create swap notification (optimistic + persist)
      const inDisplay = state.inputAmount
        ? formatAmount(state.inputAmount, inputToken.decimals)
        : '?';
      const outDisplay = state.outputAmount
        ? formatAmount(state.outputAmount, outputToken.decimals)
        : '?';

      const optimistic = buildSwapAppNotification(
        inputToken.symbol,
        outputToken.symbol,
        inDisplay,
        outDisplay,
        sig
      );
      window.dispatchEvent(
        new CustomEvent('memelaunch:notifications:optimistic', { detail: optimistic })
      );
      createSwapNotification(
        walletAddress,
        inputToken.symbol,
        outputToken.symbol,
        inDisplay,
        outDisplay,
        sig
      ).catch((err) => console.error('[SwapTab] Notification error:', err));
    }
  }, [state.status, state.signature, refetchBalance, refetchTokens]);

  // Flip tokens
  const handleFlip = () => {
    if (isExecuting) return;
    const prevInput = inputToken;
    const prevOutput = outputToken;
    setInputToken({ ...prevOutput, balance: getTokenBalance(prevOutput) });
    setOutputToken({ ...prevInput, balance: getTokenBalance(prevInput) });
    setInputAmount('');
    reset();
  };

  // Max button
  const handleMax = () => {
    if (inputBalance === undefined) return;

    let maxAmount = inputBalance;

    // Reserve SOL for tx fees
    if (inputToken.mint === SOL_TOKEN.mint) {
      maxAmount = Math.max(0, inputBalance - 0.01);
    }

    if (maxAmount <= 0) {
      setInputAmount('0');
      return;
    }

    // Format to reasonable decimal places
    const decimals = inputToken.decimals > 6 ? 6 : inputToken.decimals;
    setInputAmount(maxAmount.toFixed(decimals));
  };

  // Percentage shortcut
  const handlePercentage = (pct: number) => {
    if (inputBalance === undefined || inputBalance <= 0) return;

    let maxAmount = inputBalance;
    if (inputToken.mint === SOL_TOKEN.mint) {
      maxAmount = Math.max(0, inputBalance - 0.01);
    }
    if (maxAmount <= 0) {
      setInputAmount('0');
      return;
    }

    const amount = maxAmount * (pct / 100);
    const decimals = inputToken.decimals > 6 ? 6 : inputToken.decimals;
    setInputAmount(amount.toFixed(decimals));
  };

  // Handle swap execution
  const handleSwap = () => {
    if (!canExecute) return;
    executeSwap();
  };

  // Compute output display
  const outputDisplay = state.quote
    ? formatAmount(state.quote.outAmount, outputToken.decimals)
    : '';

  // Price impact
  const priceImpact = state.quote?.priceImpactPct
    ? parseFloat(state.quote.priceImpactPct)
    : null;

  const priceImpactColor =
    priceImpact === null
      ? ''
      : priceImpact < 1
        ? 'text-[#4ADE80]'
        : priceImpact < 5
          ? 'text-[#FBBF24]'
          : 'text-[#F87171]';

  // Exchange rate display
  const exchangeRate =
    state.quote && inputToken && outputToken
      ? (() => {
          const inAmt = parseInt(state.quote.inAmount) / Math.pow(10, inputToken.decimals);
          const outAmt = parseInt(state.quote.outAmount) / Math.pow(10, outputToken.decimals);
          if (inAmt === 0) return null;
          const rate = outAmt / inAmt;
          return `1 ${inputToken.symbol} = ${rate < 0.001 ? rate.toExponential(2) : rate.toLocaleString(undefined, { maximumFractionDigits: 4 })} ${outputToken.symbol}`;
        })()
      : null;

  // Button state
  const getButtonContent = () => {
    if (state.status === 'quoting') {
      return (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Fetching quote...
        </>
      );
    }
    if (state.status === 'signing') {
      return (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Signing...
        </>
      );
    }
    if (state.status === 'sending' || state.status === 'confirming') {
      return (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Swapping...
        </>
      );
    }
    if (state.status === 'success') {
      return (
        <>
          <Check className="h-4 w-4 mr-2" />
          Swap complete
        </>
      );
    }
    if (state.status === 'failure') {
      return (
        <>
          <AlertTriangle className="h-4 w-4 mr-2" />
          Swap failed. Try again.
        </>
      );
    }

    if (!inputAmount || parseFloat(inputAmount) <= 0) {
      return 'Enter an amount';
    }

    if (inputBalance !== undefined && parseFloat(inputAmount) > inputBalance) {
      return 'Insufficient balance';
    }

    return 'Swap';
  };

  const isButtonDisabled =
    isExecuting ||
    state.status === 'quoting' ||
    state.status === 'success' ||
    !inputAmount ||
    parseFloat(inputAmount) <= 0 ||
    (inputBalance !== undefined && parseFloat(inputAmount) > inputBalance) ||
    (!canExecute && state.status !== 'failure');

  return (
    <div className="flex flex-col gap-3">
      {/* Input section: You pay */}
      <div className="rounded-lg border border-border bg-muted/30 p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-muted-foreground">You pay</span>
          {inputBalance !== undefined && (
            <button
              type="button"
              onClick={handleMax}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Balance: {inputBalance < 0.001 && inputBalance > 0
                ? '<0.001'
                : inputBalance.toLocaleString(undefined, { maximumFractionDigits: 4 })}
            </button>
          )}
        </div>
        {inputBalance !== undefined && inputBalance > 0 && usableInputBalance >= 0.001 && (
          <div className="flex items-center gap-1.5 mb-2">
            {[25, 50, 75].map((pct) => (
              <button
                key={pct}
                type="button"
                onClick={() => handlePercentage(pct)}
                disabled={isExecuting}
                className="px-2 py-0.5 text-xs rounded border border-border text-muted-foreground hover:text-foreground hover:border-[#CCFF00]/30 hover:bg-[#CCFF00]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {pct}%
              </button>
            ))}
            <button
              type="button"
              onClick={handleMax}
              disabled={isExecuting}
              className="px-2 py-0.5 text-xs rounded border border-border text-muted-foreground hover:text-foreground hover:border-[#CCFF00]/30 hover:bg-[#CCFF00]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Max
            </button>
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            type="number"
            inputMode="decimal"
            step="any"
            min="0"
            placeholder="0"
            value={inputAmount}
            onChange={(e) => setInputAmount(e.target.value)}
            disabled={isExecuting}
            className="bg-transparent text-lg font-medium outline-none w-full placeholder:text-muted-foreground/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <SwapTokenSelector
            label="You pay"
            selected={inputToken}
            onSelect={(token) => {
              setInputToken(token);
              setInputAmount('');
              reset();
            }}
            userTokens={splTokens}
            solBalance={formattedBalance}
            disabled={isExecuting}
            excludeMint={outputToken?.mint}
          />
        </div>
      </div>

      {/* Flip button */}
      <div className="flex justify-center -my-1">
        <button
          type="button"
          onClick={handleFlip}
          disabled={isExecuting}
          className="p-1.5 rounded-lg border border-border bg-secondary hover:bg-muted/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>

      {/* Output section: You receive */}
      <div className="rounded-lg border border-border bg-muted/30 p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-muted-foreground">You receive</span>
          {outputBalance !== undefined && (
            <span className="text-xs text-muted-foreground">
              Balance: {outputBalance < 0.001 && outputBalance > 0
                ? '<0.001'
                : outputBalance.toLocaleString(undefined, { maximumFractionDigits: 4 })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`text-lg font-medium flex-1 ${
              outputDisplay ? 'text-foreground' : 'text-muted-foreground/50'
            }`}
          >
            {outputDisplay || '0'}
          </span>
          <SwapTokenSelector
            label="You receive"
            selected={outputToken}
            onSelect={(token) => {
              setOutputToken(token);
              reset();
            }}
            userTokens={splTokens}
            solBalance={formattedBalance}
            disabled={isExecuting}
            excludeMint={inputToken?.mint}
          />
        </div>
      </div>

      {/* Quote details */}
      {state.quote && state.status === 'ready' && (
        <div className="rounded-lg border border-border p-3 space-y-1.5">
          {exchangeRate && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Rate</span>
              <span className="text-xs text-foreground">{exchangeRate}</span>
            </div>
          )}
          {priceImpact !== null && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Price impact</span>
              <span className={`text-xs ${priceImpactColor}`}>
                {priceImpact < 0.01 ? '<0.01' : priceImpact.toFixed(2)}%
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Platform fee</span>
            <span className="text-xs text-muted-foreground">Free</span>
          </div>
        </div>
      )}

      {/* Error display */}
      {state.error && state.status === 'failure' && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
          <p className="text-xs text-destructive">{state.error}</p>
          {state.signature && (
            <button
              type="button"
              onClick={() => window.open(getSolscanUrl(state.signature!), '_blank')}
              className="flex items-center gap-1 mt-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ExternalLink className="h-3 w-3" />
              View on Solscan
            </button>
          )}
        </div>
      )}

      {/* Swap button */}
      <Button
        className="w-full"
        onClick={state.status === 'failure' ? reset : handleSwap}
        disabled={state.status === 'failure' ? false : isButtonDisabled}
      >
        {getButtonContent()}
      </Button>
    </div>
  );
};
